/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';

//----------------------------------------------------------
// @CUSTOM: Imports for customizations
//----------------------------------------------------------
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { clsHelper } from '../../../utilities/class-name-helper';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Class name utility for customizations that
// want to use the same base class
//----------------------------------------------------------
const BASE_CLASS = 'ms-header';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);
//----------------------------------------------------------

// @CUSTOM: Added extended config props
const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = props => {
    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer,
        config,         // @CUSTOM: Added extended config
        // Divider      // @CUSTOM: Removed
    } = props;
    return (
        <Module {...HeaderTag}>
            {/* @CUSTOM: TSX below has been reorganized for the theme, with some unnecessary elements commented out in case they need to be brought back */}
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    <div className={cls('topbar-container')}>
                        <div className={cls('topbar-left')}>
                            {props.navIcon}
                            {props.logo}
                            {!!(config.logoText || config.logoSubtext) && (
                                <div className={cls('logo-caption')}>
                                    {config.logoText && <div className={cls('logo-caption-text')}>{config.logoText}</div>}
                                    {config.logoSubtext && <div className={cls('logo-caption-subtext')}>{config.logoSubtext}</div>}
                                </div>
                            )}
                        </div>
                        <div className={cls('topbar-right')}>
                            {_renderReactFragment(props.search)}
                            {/* {props.preferredStore} */}
                            {/* {props.currentDistributor} */}
                            {/* {props.wishListIconDesktop} */}
                            {/* <Node {...Divider} /> */}
                            <div className={cls('topbar-actions')}>
                                {_renderDesktopAccountBlock(props)}
                                {props.cartIcon}
                                {/* {_renderReactFragment(props.siteOptions)} */}
                            </div>
                        </div>
                    </div>
                </Node>
                <div className={cls('bottombar')}>
                    <div className={cls('bottombar-container')}>
                        {_renderReactFragment(props.menuBar)}
                    </div>
                </div>
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        {props.MobileMenuHeader}
                        {_renderReactFragment(props.menuBar)}
                        <Node {...MobileMenuLinksContainer}>
                            {props.accountLinks ? props.accountLinks.map(link => link) : false}
                            {/* {props.siteOptions} */}
                            {/* {props.wishListIconMobile} */}
                            {props.signInLink}
                            {props.signOutLink}
                        </Node>
                    </Node>
                </Node>
            </Node>
            {/*-------------------------------------------------------*/}
        </Module>
    );
};

function _renderDesktopAccountBlock(props: IHeaderViewProps): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map(link => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return <Node {...AccountInfoDropdownParentContainer}>{signInLink}</Node>;
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

export default headerView;
